import {
  Accordion,
  Box,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
} from '@chakra-ui/react';
import { t } from 'i18next';
import { useCallback } from 'react';
import { TableInstance } from 'react-table';
import { FilterOption } from '~components/organization/ZteTable/types';
import { ZteFilters } from '~graphql/__generated__/types';
import { ZteUiFilters } from '~pages/[organization]/settings/zte/Index';
import { ZteTableFragment } from '../__generated__/ZteTable.graphql';
import { FilterButton } from './FilterButton';
import { FilterSection } from './FilterSection';
import { FilterTagList } from './FilterTags';
import { useFilterOptions } from './useFilterOptions';

type Props = {
  table: TableInstance<ZteTableFragment>;
  data: ZteTableFragment[];
  dataWithFilters: ZteFilters;
  filtersState: ZteUiFilters;
  handleSetFilters: (filterId: string, newValue: FilterOption[]) => void;
};

export function Filter({ dataWithFilters, filtersState, handleSetFilters }: Props) {
  const filters = useFilterOptions(filtersState, dataWithFilters);
  const allEnabledFilters = [
    ...filters.batchName.enabled,
    ...filters.status.enabled,
    ...filters.customerName.enabled,
    ...filters.modelNumber.enabled,
  ];

  const toggleFilter = useCallback(
    (filter: FilterOption) => {
      const currentFilter = filtersState.find((f) => f.id === filter.column);
      const isEnabled = currentFilter?.value.some((v) => v.value === filter.value);

      if (isEnabled) {
        handleSetFilters(
          filter.column,
          currentFilter?.value.filter((v) => v.value !== filter.value) ?? [],
        );
      } else {
        handleSetFilters(filter.column, [...(currentFilter?.value ?? []), filter]);
      }
    },
    [filtersState, handleSetFilters],
  );

  const disableFilter = useCallback(
    (filter: FilterOption) => {
      const currentFilter = filtersState.find((f) => f.id === filter.column);
      handleSetFilters(
        filter.column,
        currentFilter?.value.filter((v) => v.value !== filter.value) ?? [],
      );
    },
    [filtersState, handleSetFilters],
  );

  const resetAllFilters = useCallback(() => {
    filtersState.forEach((filter) => handleSetFilters(filter.id, []));
  }, [filtersState, handleSetFilters]);

  return (
    <Box display="flex" alignItems="center">
      <Popover closeOnBlur closeOnEsc placement="bottom-start" isLazy>
        <PopoverTrigger>
          <FilterButton enabledFilterCount={allEnabledFilters.length} />
        </PopoverTrigger>

        <PopoverContent color="gray.900" width="430px">
          <PopoverBody>
            <Accordion allowToggle>
              <FilterSection
                name={t('customer')}
                filters={filters.customerName}
                onToggle={toggleFilter}
              />
              <FilterSection
                name={t('batchId')}
                filters={filters.batchName}
                onToggle={toggleFilter}
              />
              <FilterSection
                name={t('modelNo')}
                filters={filters.modelNumber}
                onToggle={toggleFilter}
              />
              <FilterSection name={t('status')} filters={filters.status} onToggle={toggleFilter} />
            </Accordion>
          </PopoverBody>
        </PopoverContent>
      </Popover>

      <Box marginLeft="4">
        <FilterTagList
          filters={allEnabledFilters}
          onDisable={disableFilter}
          onReset={resetAllFilters}
        />
      </Box>
    </Box>
  );
}
